// ─── ClientesSilencieuses.jsx ────────────────────────────────────────
// Qui n'a pas ouvert son espace depuis un moment.
//
// Anissa voyait cette information fiche par fiche, ce qui obligeait à ouvrir
// dix dossiers pour se faire une idée. Ce qui a de la valeur pour elle n'est
// pas « Clélia s'est connectée à 14h », c'est « trois clientes n'ont pas
// ouvert leur espace depuis deux semaines ».
//
// ⚠ Ce n'est pas de la surveillance. On n'affiche jamais ce qu'une cliente
// fait dans l'application, seulement si elle y est venue. Voir
// `clientesSilencieuses.js` pour les deux garde-fous : on ne compte que les
// programmes publiés, et on sépare « jamais venue » de « ne revient plus ».

import { useEffect, useState } from 'react';
import { fetchClientsStatus } from '../services/fetchClientsStatus';
import {
  SEUIL_SILENCE_JOURS,
  clientesSilencieuses,
  formulerSilence,
} from '../services/clientesSilencieuses';

export default function ClientesSilencieuses({ clients = [], onClickClient, refreshTick }) {
  const [silence, setSilence] = useState(null);
  const [replie, setReplie] = useState(false);

  useEffect(() => {
    let annule = false;
    const avecApp = (Array.isArray(clients) ? clients : []).filter((c) => c?.app_enabled);
    if (avecApp.length === 0) { setSilence(null); return undefined; }

    fetchClientsStatus(avecApp.map((c) => ({
      email: c?.form?.email || c?.email || null,
      stagingClientId: c?.stagingClientId || c?.staging_client_id || null,
    })))
      .then((map) => {
        if (annule) return;
        const observees = avecApp.map((c) => {
          const cle = c?.stagingClientId || c?.staging_client_id;
          const statut = (cle && map[`id:${cle}`])
            || map[String(c?.form?.email || c?.email || '').toLowerCase()]
            || null;
          return {
            id: c.id,
            prenom: c?.form?.prenom || c?.prenom || 'Cliente',
            programmePublie: statut?.plan_status === 'published' || Boolean(statut?.published_at),
            publieLe: statut?.published_at || null,
            derniereConnexion: statut?.last_login_at || null,
          };
        });
        setSilence(clientesSilencieuses(observees));
      })
      // Silencieux à dessein : cet encart est un confort. S'il ne peut pas
      // se calculer, il disparaît, il ne casse pas le tableau de bord et
      // n'affiche surtout pas une liste vide qui voudrait dire « tout va
      // bien » alors qu'on n'a pas pu regarder.
      .catch(() => { if (!annule) setSilence(null); });

    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, refreshTick]);

  if (!silence || silence.total === 0) return null;

  const ligne = (c, sousTitre) => (
    <div
      key={c.id}
      onClick={() => onClickClient?.(c.id)}
      role={onClickClient ? 'button' : undefined}
      tabIndex={onClickClient ? 0 : undefined}
      onKeyDown={onClickClient ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClickClient(c.id); }
      } : undefined}
      className={`silent-clients__row${onClickClient ? ' silent-clients__row--interactive' : ''}`}
    >
      <span className="silent-clients__name">{c.prenom}</span>
      <span className="silent-clients__detail">{sousTitre(c)}</span>
    </div>
  );

  return (
    <section className="attention-card attention-card--warm silent-clients">
      <button
        type="button"
        onClick={() => setReplie((v) => !v)}
        className="silent-clients__toggle"
        aria-expanded={!replie}
        title={replie ? 'Afficher' : 'Masquer'}
      >
        <span className="silent-clients__title">
          Clientes silencieuses
        </span>
        <span className="silent-clients__count">
          {silence.total} cliente{silence.total > 1 ? 's' : ''}
        </span>
        <span className="silent-clients__chevron" aria-hidden="true">{replie ? '+' : '−'}</span>
      </button>

      {!replie && (
        <div className="silent-clients__body">
          {silence.jamaisVenues.length > 0 && (
            <div className={silence.decrochees.length > 0 ? 'silent-clients__group silent-clients__group--spaced' : 'silent-clients__group'}>
              <p className="silent-clients__label">
                Jamais ouvert leur espace
              </p>
              {silence.jamaisVenues.map((c) => ligne(c,
                (x) => `programme publié il y a ${formulerSilence(x.jours)}`))}
              <p className="silent-clients__hint">
                Souvent un lien de connexion qui n’est jamais arrivé. Un message suffit
                en général.
              </p>
            </div>
          )}

          {silence.decrochees.length > 0 && (
            <div className="silent-clients__group">
              <p className="silent-clients__label">
                Ne sont pas revenues depuis {SEUIL_SILENCE_JOURS} jours
              </p>
              {silence.decrochees.map((c) => ligne(c,
                (x) => `vue il y a ${formulerSilence(x.jours)}`))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
